import React from 'react';
import usePageSEO from '@/hooks/usePageSEO';

const PrivacyPolicy = () => {
  usePageSEO({
    title: "Politique de confidentialité | Bio Beauté Zen",
    description: "Politique de confidentialité de Bio Beauté Zen : collecte, utilisation et protection de vos données personnelles conformément au RGPD.",
    canonical: "https://biobeautezen.com/politique-confidentialite",
    noindex: true
  });
  
  return <div className="pt-24 pb-16">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-3xl font-playfair font-bold text-bbz-text mb-8">Politique de Confidentialité</h1>
        
        <div className="space-y-8">
          <section>
            <h2 className="text-xl font-semibold mb-4">Introduction</h2>
            <div className="bg-bbz-beige/10 p-6 rounded-lg">
              <p className="mb-4">
                Bio Beauté Zen s'engage à protéger la vie privée des utilisateurs de son site web et à traiter les données personnelles conformément 
                au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés.
              </p>
              <p>
                La présente politique de confidentialité vous informe sur la façon dont nous recueillons, utilisons et protégeons vos informations personnelles.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">Collecte des données</h2>
            <div className="bg-bbz-beige/10 p-6 rounded-lg">
              <p className="mb-4">Nous collectons différents types d'informations personnelles, notamment :</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Informations d'identification (nom, prénom, adresse email, numéro de téléphone)</li>
                <li>Informations relatives aux rendez-vous (date, heure, services choisis, préférences)</li>
                <li>Messages envoyés via notre formulaire de contact</li>
                <li>Données techniques liées à votre navigation (adresse IP, type de navigateur, pages visitées)</li>
              </ul>
              <p>
                Ces données sont collectées lorsque vous interagissez avec notre site, prenez rendez-vous, 
                ou nous contactez via le formulaire de contact.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">Utilisation des données</h2>
            <div className="bg-bbz-beige/10 p-6 rounded-lg">
              <p className="mb-4">Nous utilisons vos données personnelles pour :</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Gérer vos rendez-vous et vous fournir les services demandés</li>
                <li>Vous envoyer des confirmations et rappels de rendez-vous</li>
                <li>Répondre à vos demandes de renseignements</li>
                <li>Améliorer nos services et votre expérience utilisateur</li>
                <li>Respecter nos obligations légales et réglementaires</li>
              </ul>
              <p>
                Nous ne vendons ni ne louons vos données personnelles à des tiers.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">Stockage et sécurité des données</h2>
            <div className="bg-bbz-beige/10 p-6 rounded-lg">
              <p className="mb-4">
                Vos données personnelles sont stockées de manière sécurisée sur les serveurs de Supabase, 
                notre prestataire d'hébergement et de base de données.
              </p>
              <p className="mb-4">
                Des mesures de sécurité techniques et organisationnelles appropriées sont mises en place pour protéger 
                vos données contre tout accès, utilisation, modification ou divulgation non autorisés.
              </p>
              <p>
                Nous conservons vos données personnelles uniquement pendant la durée nécessaire aux finalités pour lesquelles 
                elles ont été collectées, conformément aux obligations légales applicables.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">Partage des données</h2>
            <div className="bg-bbz-beige/10 p-6 rounded-lg">
              <p className="mb-4">
                Dans le cadre de nos activités, nous pouvons partager vos données avec :
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Notre prestataire d'hébergement et de base de données (Supabase)</li>
                <li>Notre service d'envoi d'emails (Resend)</li>
              </ul>
              <p className="mb-4">
                Ces prestataires agissent en tant que sous-traitants et sont tenus de respecter la confidentialité 
                et la sécurité de vos données conformément au RGPD.
              </p>
              <p>
                Nous ne transférons pas vos données personnelles en dehors de l'Union européenne, sauf si cela est nécessaire 
                pour l'exécution de nos services et dans le respect des garanties appropriées prévues par la réglementation.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">Vos droits</h2>
            <div className="bg-bbz-beige/10 p-6 rounded-lg">
              <p className="mb-4">
                Conformément au RGPD, vous disposez des droits suivants concernant vos données personnelles :
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Droit d'accès et de rectification</li>
                <li>Droit à l'effacement (droit à l'oubli)</li>
                <li>Droit à la limitation du traitement</li>
                <li>Droit à la portabilité des données</li>
                <li>Droit d'opposition</li>
                <li>Droit de retirer votre consentement à tout moment</li>
              </ul>
              <p className="mb-4">Pour exercer ces droits, vous pouvez nous contacter à l'adresse email suivante : biobeautezen.institut@gmail.com</p>
              <p>
                Vous avez également le droit d'introduire une réclamation auprès de la Commission Nationale de l'Informatique 
                et des Libertés (CNIL) si vous estimez que le traitement de vos données n'est pas conforme à la réglementation.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">Cookies</h2>
            <div className="bg-bbz-beige/10 p-6 rounded-lg space-y-4">
              <p>
                Notre site utilise des cookies et technologies similaires pour améliorer votre expérience de navigation et 
                analyser l'utilisation du site. Les cookies sont de petits fichiers texte stockés sur votre appareil qui nous 
                permettent de reconnaître votre navigateur et de mémoriser certaines informations.
              </p>
              
              <div>
                <h3 className="font-semibold mb-2">Cookies essentiels (obligatoires)</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>
                    <strong>bbz-cookie-consent</strong> : Stocke vos préférences concernant l'utilisation des cookies. 
                    Durée : 13 mois. Ce cookie est indispensable au respect de votre choix et ne nécessite pas de consentement.
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Cookies analytiques (optionnels - nécessitent votre consentement)</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>
                    <strong>Google Analytics (_ga, _gid)</strong> : Ces cookies me permettent d'analyser le trafic de mon site, 
                    de comprendre comment les visiteurs utilisent mes pages et d'améliorer leurs fonctionnalités. 
                    Les données sont collectées de manière anonymisée (adresse IP anonymisée). Durée : 13 mois maximum.
                  </li>
                </ul>
                <p className="mt-2 text-sm">
                  Vous pouvez accepter ou refuser ces cookies via le bandeau qui apparaît lors de votre première visite.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Gestion de vos préférences</h3>
                <p>
                  Vous pouvez à tout moment modifier vos préférences de cookies en cliquant sur le lien 
                  <strong> "Gérer les cookies"</strong> disponible en bas de chaque page. Vous pouvez également 
                  configurer votre navigateur pour refuser tous les cookies, mais cela pourrait affecter certaines 
                  fonctionnalités du site.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Durée de conservation du consentement</h3>
                <p>
                  Votre choix concernant les cookies est conservé pendant 13 mois, conformément aux recommandations de la CNIL. 
                  Passé ce délai, le bandeau vous sera à nouveau présenté pour recueillir votre consentement.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">Modifications de la politique de confidentialité</h2>
            <div className="bg-bbz-beige/10 p-6 rounded-lg">
              <p className="mb-4">
                Nous nous réservons le droit de modifier la présente politique de confidentialité à tout moment, 
                notamment pour l'adapter aux évolutions légales et réglementaires.
              </p>
              <p>
                Les modifications prennent effet dès la publication de la nouvelle version sur notre site. 
                Nous vous encourageons à consulter régulièrement cette page pour rester informé des mises à jour.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">Contact</h2>
            <div className="bg-bbz-beige/10 p-6 rounded-lg">
              <p>Pour toute question concernant notre politique de confidentialité, vous pouvez nous contacter à l'adresse email suivante : biobeautezen.institut@gmail.com</p>
            </div>
          </section>

          <p className="text-sm text-bbz-text/70 mt-8">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </div>
      </div>
    </div>;
};

export default PrivacyPolicy;
